import { c as _c } from "react/compiler-runtime";
// `<OgAvatar>` — user avatar for OG cards.
//
// Renders an `<img>` directly with an initials-disc fallback underneath
// (the disc shows through if the upstream image fails to decode). The
// initials hash and 14-colour palette match
// `frontend/src/components/profile/user-avatar.tsx` exactly so a user
// lands on the same colour in-app and on shared cards.
//
// Why not `<OgImage>`?  The general-purpose primitive locks `src` to
// URLs ending in `.png` / `.jpg` — but real user avatars (Twitter,
// Google, S3, IPFS) almost always lack a file extension
// (`pbs.twimg.com/profile_images/.../abc`,
// `lh3.googleusercontent.com/...`, signed S3 keys, etc.). The strict
// regex falls back to the initials disc even when the upstream image
// is a perfectly decodable JPEG. Avatars accept any absolute http(s)
// URL; Satori silently renders empty space if the fetch / decode flops,
// at which point the disc behind the image becomes the visible state.
//
// Tailwind class names don't render inside Satori, so the palette here
// uses hex values that mirror the app's Tailwind `-500` shades.

import type { CSSProperties } from 'react';
import { getOgAvatarInitials, getOgAvatarPaletteEntry, resolveOgAvatarSeed } from './og-avatar-utils';
import { registerImageUrl } from './register-image-url';

/**
 * Avatar-specific renderable check. Allows any absolute http(s) URL or
 * PNG/JPEG data URL. Permissive on purpose — see header comment.
 */
function isAvatarRenderableSrc(src: string | null | undefined): src is string {
  if (typeof src !== 'string' || src.length === 0) return false;
  if (src.startsWith('data:image/png') || src.startsWith('data:image/jpeg')) return true;
  return src.startsWith('https://') || src.startsWith('http://');
}
export type OgAvatarSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export interface OgAvatarProps {
  /** Primary identity for initials + palette hash. */
  displayName: string | null;
  /** Morfi handle. When `displayName` is null, falls back to the
   *  handle for both initials (e.g. `wenlauch` → `WE`) and the
   *  deterministic palette colour. Mirrors the in-app fallback so a
   *  handle-only user lands on a coloured disc instead of the neutral
   *  "AN" placeholder. */
  handle?: string | null;
  avatarUrl: string | null | undefined;
  size?: OgAvatarSize;
  /** Override the rendered pixel size. Use sparingly — pick a size token first. */
  pixelSize?: number;
}
const SIZE_PX: Record<OgAvatarSize, number> = {
  sm: 24,
  md: 40,
  lg: 64,
  xl: 96,
  '2xl': 144
} as const;
export function OgAvatar(props) {
  const $ = _c(25);
  const px = props.pixelSize ?? SIZE_PX[props.size ?? "md"];
  let initials;
  let seed;
  let t0;
  if ($[0] !== props.displayName || $[1] !== props.handle) {
    seed = resolveOgAvatarSeed(props.displayName, props.handle ?? null);
    initials = getOgAvatarInitials(seed);
    t0 = getOgAvatarPaletteEntry(seed);
    $[0] = props.displayName;
    $[1] = props.handle;
    $[2] = initials;
    $[3] = seed;
    $[4] = t0;
  } else {
    initials = $[2];
    seed = $[3];
    t0 = $[4];
  }
  const palette = t0;
  const t1 = palette.bg;
  const t2 = palette.text;
  let t3;
  if ($[5] !== px) {
    t3 = Math.round(px * 0.42);
    $[5] = px;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== palette.bg || $[8] !== palette.text || $[9] !== px || $[10] !== t3) {
    t4 = {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: px,
      height: px,
      borderRadius: 9999,
      background: t1,
      color: t2,
      fontSize: t3,
      fontWeight: 700,
      letterSpacing: -0.5,
      overflow: "hidden"
    };
    $[7] = palette.bg;
    $[8] = palette.text;
    $[9] = px;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const discStyle = t4;
  if (!isAvatarRenderableSrc(props.avatarUrl)) {
    let t5;
    if ($[12] !== discStyle || $[13] !== initials) {
      t5 = <div style={discStyle}>{initials}</div>;
      $[12] = discStyle;
      $[13] = initials;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    return t5;
  }
  registerImageUrl(props.avatarUrl);
  const t5 = seed ?? "Avatar";
  let t6;
  if ($[15] !== px) {
    t6 = {
      width: px,
      height: px,
      objectFit: "cover",
      borderRadius: 9999
    };
    $[15] = px;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== props.avatarUrl || $[18] !== px || $[19] !== t5 || $[20] !== t6) {
    t7 = <img src={props.avatarUrl} alt={t5} width={px} height={px} style={t6} />;
    $[17] = props.avatarUrl;
    $[18] = px;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== discStyle || $[23] !== t7) {
    t8 = <div style={discStyle}>{t7}</div>;
    $[22] = discStyle;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
}